import { Swiper, Navigation } from 'swiper';

export class Chat extends HTMLElement {
  constructor() {
    super();
    this.chatButton = null;
    this.singleCarFooterChatButton = null;
    this.chatContent = null;
    this.closeButton = null;
    this.resetButton = null;
    this.contentBody = null;
    this.messagesContainer = null;
    this.inputField = null;
    this.sendButton = null;
    this.concessionSelect = null;
    this.isOpen = false;
    this.firstOpen = true;
    this.conversationHistory = [];
    this.uiHistory = [];
    this.isWaitingForResponse = false;
    this.vehicleResultsSwiper = null;
    this.resetModal = null;
    this.agentName = 'Julie';
    this.concessionId = '';
    this.concessionName = '';
    this.concessions = [];
    this.storageVersion = 1;
    this.apiBase = window.location.origin;
    this.mode = 'hub';
    this.inventoryUrl = '/inventaire/';
    this.inventoryUrlNew = '';
    this.inventoryUrlUsed = '';
    this.disableVehicleSearch = false;
    this.hideConcessionSelect = false;
    this._initialized = false;
    this._onToggleClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggleChat();
    };
    this._onFooterChatClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.openChat();
      document.documentElement.classList.add('is-chat-open');
    };
    this._onCloseClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.closeChat();
    };
    this._onResetClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.showResetModal();
    };
    this._onConcessionChange = () => this.handleConcessionChange();
    this._onInputKeydown = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.handleSend();
      }
    };
    this._onSendClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.handleSend();
    };
  }

  connectedCallback() {
    this.agentName = this.getAttribute('data-agent-name') || 'Julie';
    this.apiBase = (this.getAttribute('data-api-base') || window.location.origin).replace(/\/$/, '');
    this.mode = this.getAttribute('data-mode') === 'embed' ? 'embed' : 'hub';
    this.inventoryUrlNew = this.getAttribute('data-inventory-url-new') || '';
    this.inventoryUrlUsed = this.getAttribute('data-inventory-url-used') || '';
    // Legacy single URL still supported as fallback for both.
    const legacyInventoryUrl = this.getAttribute('data-inventory-url') || '';
    if (!this.inventoryUrlNew && legacyInventoryUrl) {
      this.inventoryUrlNew = legacyInventoryUrl;
    }
    if (!this.inventoryUrlUsed && legacyInventoryUrl) {
      this.inventoryUrlUsed = legacyInventoryUrl;
    }
    this.inventoryUrl =
      this.inventoryUrlUsed || this.inventoryUrlNew || legacyInventoryUrl || '/inventaire/';
    this.disableVehicleSearch =
      this.getAttribute('data-disable-vehicle-search') === '1' || this.mode === 'embed';
    this.hideConcessionSelect =
      this.getAttribute('data-hide-concession-select') === '1' || this.mode === 'embed';
    this.parseConcessions();
    // During HTML parsing, connectedCallback can run before light-DOM children exist.
    this.whenReady(() => this.init());
  }

  whenReady(callback) {
    if (this.querySelector('.c-chat_button') && this.querySelector('.c-chat_content')) {
      callback();
      return;
    }

    const run = () => {
      if (this.querySelector('.c-chat_button') && this.querySelector('.c-chat_content')) {
        callback();
        return;
      }
      requestAnimationFrame(run);
    };

    // Defer past current parser turn / microtasks.
    setTimeout(run, 0);
  }

  parseConcessions() {
    try {
      const raw = this.getAttribute('data-concessions') || '[]';
      this.concessions = JSON.parse(raw);
      if (!Array.isArray(this.concessions)) {
        this.concessions = [];
      }
    } catch (error) {
      console.warn('Unable to parse chat concessions', error);
      this.concessions = [];
    }
  }

  getCurrentLocale() {
    const langAttr = document.documentElement.getAttribute('lang');
    if (!langAttr) return 'fr';
    return langAttr.slice(0, 2).toLowerCase() === 'en' ? 'en' : 'fr';
  }

  t(fr, en) {
    return this.getCurrentLocale() === 'en' ? en : fr;
  }

  init() {
    if (this._initialized) {
      return;
    }
    this._initialized = true;

    this.chatButton = this.querySelector('.c-chat_button');
    this.singleCarFooterChatButton =
      document.querySelector('.c-single-car-footer__chat') ||
      document.querySelector('.c-single-car-footer_chat');
    this.chatContent = this.querySelector('.c-chat_content');
    this.closeButton = this.querySelector('.c-chat_toolbar_close');
    this.resetButton = this.querySelector('.c-chat_toolbar_reset');
    this.contentBody = this.querySelector('.c-chat_content_body');
    this.messagesContainer = this.querySelector('.c-chat_messages_container');
    this.inputField = this.querySelector('.c-chat_user_input_field');
    this.sendButton = this.querySelector('.c-chat_user_input_button');
    this.concessionSelect = this.querySelector('.c-chat_concession_select');

    this.classList.remove('is-open');
    if (this.chatContent) {
      this.chatContent.classList.add('is-not-active');
      this.chatContent.classList.remove('is-active');
    }

    this.resolveInitialConcession();
    this.syncConcessionUi();

    if (this.hideConcessionSelect) {
      const concessionBlock = this.querySelector('.c-chat_concession');
      if (concessionBlock) {
        concessionBlock.hidden = true;
        concessionBlock.style.display = 'none';
      }
      this.classList.add('is-embed');
    }

    this.chatButton?.addEventListener('click', this._onToggleClick);
    this.singleCarFooterChatButton?.addEventListener('click', this._onFooterChatClick);
    this.closeButton?.addEventListener('click', this._onCloseClick);
    this.resetButton?.addEventListener('click', this._onResetClick);
    this.concessionSelect?.addEventListener('change', this._onConcessionChange);
    this.inputField?.addEventListener('keydown', this._onInputKeydown);
    this.sendButton?.addEventListener('click', this._onSendClick);

    if (this.concessionId) {
      this.restoreChatFromStorage();
    }

    this.updateInputAvailability();
  }

  resolveInitialConcession() {
    let selected = this.getAttribute('data-selected-concession') || '';

    if (!selected && typeof window.dealerId !== 'undefined' && window.dealerId) {
      selected = String(window.dealerId);
    }

    if (!selected) {
      const dealerEl = document.querySelector('.js-current-dealer[data-id]');
      if (dealerEl?.dataset?.id) {
        selected = String(dealerEl.dataset.id);
      }
    }

    if (selected && this.concessions.some((c) => String(c.id) === String(selected))) {
      this.setConcession(selected, false);
    } else {
      this.concessionId = '';
      this.concessionName = '';
    }
  }

  setConcession(id, resetConversation = false) {
    const match = this.concessions.find((c) => String(c.id) === String(id));
    if (match) {
      this.concessionId = String(match.id);
      this.concessionName = match.title;
    } else if (id) {
      // Embed / fixed concession not present in list — name comes from API bootstrap.
      this.concessionId = String(id);
      this.concessionName = this.concessionName || '';
    } else {
      this.concessionId = '';
      this.concessionName = '';
    }

    if (this.concessionSelect && this.concessionSelect.value !== this.concessionId) {
      this.concessionSelect.value = this.concessionId;
    }

    if (resetConversation) {
      this.resetChat(false);
    }

    this.updateInputAvailability();
  }

  syncConcessionUi() {
    if (this.concessionSelect && this.concessionId) {
      this.concessionSelect.value = this.concessionId;
    }
  }

  handleConcessionChange() {
    const nextId = this.concessionSelect?.value || '';
    if (nextId === this.concessionId) {
      return;
    }

    const hadHistory =
      this.conversationHistory.length > 0 || this.uiHistory.length > 0;

    if (hadHistory) {
      this.clearChatStorage();
    }

    this.setConcession(nextId, true);

    if (this.isOpen || hadHistory) {
      this.firstOpen = true;
      if (this.isOpen && this.concessionId) {
        this.firstOpen = false;
        this.showWelcomeSequence();
      }
    }
  }

  updateInputAvailability() {
    const hasConcession = !!this.concessionId;
    const enabled = hasConcession && !this.isWaitingForResponse;
    this.setInputEnabled(enabled);

    if (this.inputField) {
      this.inputField.placeholder = hasConcession
        ? this.t('Écrivez votre message...', 'Write your message...')
        : this.t(
            'Choisissez une concession pour commencer…',
            'Choose a dealership to start…'
          );
    }
  }

  toggleChat() {
    this.isOpen ? this.closeChat() : this.openChat();
  }

  openChat() {
    if (!this.chatContent || this.isOpen) return;
    this.isOpen = true;
    this.classList.add('is-open');
    this.chatContent.classList.remove('is-not-active');
    this.chatContent.classList.add('is-active');

    if (this.firstOpen) {
      this.firstOpen = false;
      if (this.concessionId) {
        if (!this.uiHistory.length) {
          this.showWelcomeSequence();
        }
      } else {
        this.showSelectConcessionHint();
      }
    }

    // Delay focus so the open animation/state settles (avoids extension form flicker).
    setTimeout(() => {
      if (!this.isOpen) return;
      if (this.concessionId) {
        this.inputField?.focus({ preventScroll: true });
      } else {
        this.concessionSelect?.focus({ preventScroll: true });
      }
    }, 350);
  }

  closeChat() {
    if (!this.chatContent || !this.isOpen) return;
    this.isOpen = false;
    this.classList.remove('is-open');
    this.chatContent.classList.add('is-not-active');
    this.chatContent.classList.remove('is-active');
    document.documentElement.classList.remove('is-chat-open');
  }

  showSelectConcessionHint() {
    if (!this.messagesContainer) return;
    this.messagesContainer.innerHTML = '';
    this.appendAgentMessage(
      this.t(
        'Choisissez une concession ci-dessus pour que je puisse vous aider.',
        'Please choose a dealership above so I can help you.'
      ),
      false,
      false
    );
  }

  showResetModal() {
    if (!this.chatContent) return;
    if (!this.resetModal) this.createResetModal();
    this.resetModal?.classList.add('is-visible');
  }

  createResetModal() {
    const overlay = document.createElement('div');
    overlay.className = 'c-chat_reset_modal_overlay';

    const content = document.createElement('div');
    content.className = 'c-chat_reset_modal_content';

    const title = document.createElement('h3');
    title.className = 'c-chat_reset_modal_title';
    title.textContent = this.t('Commencer un nouveau chat ?', 'Start a new chat?');

    const msg = document.createElement('p');
    msg.className = 'c-chat_reset_modal_message';
    msg.textContent = this.t(
      'Voulez-vous vraiment commencer un nouveau chat ? Toute la conversation actuelle sera effacée.',
      'Do you really want to start a new chat? The current conversation will be erased.'
    );

    const buttons = document.createElement('div');
    buttons.className = 'c-chat_reset_modal_buttons';

    const yesBtn = document.createElement('button');
    yesBtn.type = 'button';
    yesBtn.className = 'c-chat_reset_modal_button c-chat_reset_modal_button_yes';
    yesBtn.textContent = this.t('Oui', 'Yes');
    yesBtn.addEventListener('click', () => {
      this.closeResetModal();
      this.resetChat(true);
    });

    const noBtn = document.createElement('button');
    noBtn.type = 'button';
    noBtn.className = 'c-chat_reset_modal_button c-chat_reset_modal_button_no';
    noBtn.textContent = this.t('Non', 'No');
    noBtn.addEventListener('click', () => this.closeResetModal());

    buttons.append(yesBtn, noBtn);
    content.append(title, msg, buttons);
    overlay.appendChild(content);

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this.closeResetModal();
    });

    this.chatContent?.appendChild(overlay);
    this.resetModal = overlay;
  }

  closeResetModal() {
    this.resetModal?.classList.remove('is-visible');
  }

  resetChat(showWelcome = true) {
    if (this.vehicleResultsSwiper) {
      this.vehicleResultsSwiper.destroy(true, true);
      this.vehicleResultsSwiper = null;
    }

    this.conversationHistory = [];
    this.uiHistory = [];
    this.isWaitingForResponse = false;

    if (this.messagesContainer) {
      this.messagesContainer.innerHTML = '';
    }

    this.clearChatStorage();

    if (showWelcome && this.concessionId) {
      this.showWelcomeSequence();
    } else if (showWelcome && !this.concessionId) {
      this.showSelectConcessionHint();
    }

    this.updateInputAvailability();
  }

  async showWelcomeSequence() {
    this.appendAgentTyping();

    let welcomeText = '';
    try {
      if (this.mode === 'embed' || this.disableVehicleSearch) {
        const data = await this.fetchWelcomeFromApi();
        if (data?.concession_name) {
          this.concessionName = data.concession_name;
        }
        welcomeText = data?.reply || '';
      }
    } catch (error) {
      console.warn('Unable to fetch chat welcome', error);
    }

    if (!welcomeText) {
      const name = this.concessionName || this.t('Le Prix du Gros', 'Le Prix du Gros');
      welcomeText = this.t(
        `Bienvenue chez ${name}! Comment puis-je vous aider aujourd'hui ?`,
        `Welcome to ${name}! How can I help you today?`
      );
    }

    setTimeout(() => {
      this.removeTypingIndicator();
      this.appendAgentMessage(welcomeText, false, true);
      this.appendQuickActions();
      this.scrollToBottom();
    }, this.mode === 'embed' ? 300 : 1500);
  }

  async fetchWelcomeFromApi() {
    const url = `${this.apiBase}/wp-json/lpdg/v1/chat`;
    const body = {
      bootstrap: true,
      concession_id: Number(this.concessionId),
      lang: this.getCurrentLocale(),
      mode: this.mode === 'embed' ? 'embed' : undefined,
    };

    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      throw new Error(`Welcome API error: ${res.status}`);
    }

    return res.json();
  }

  getQuickActions() {
    const actions = [
      {
        label: 'Prendre un RDV',
        labelEn: 'Make an appointment',
        message: 'Je voudrais prendre un rendez-vous.',
        messageEn: 'I would like to make an appointment.',
        icon: '🗓️',
      },
      {
        label: 'Conseillez-moi un véhicule',
        labelEn: 'Recommend a vehicle',
        message: this.disableVehicleSearch
          ? 'Je cherche un véhicule, pouvez-vous me diriger vers l’inventaire ?'
          : 'Pouvez-vous me conseiller un véhicule ?',
        messageEn: this.disableVehicleSearch
          ? 'I am looking for a vehicle, can you point me to the inventory?'
          : 'Can you recommend a vehicle?',
        icon: '🚗',
      },
      {
        label: 'Financement',
        labelEn: 'Financing',
        message: "J'aimerais en savoir plus sur le financement.",
        messageEn: 'I would like to learn more about financing.',
        icon: '💸',
      },
      {
        label: 'Évaluer mon échange',
        labelEn: 'Evaluate my trade-in',
        message: "Je voudrais faire évaluer mon véhicule d'échange.",
        messageEn: 'I would like to get a trade-in evaluation.',
        icon: '🤝',
      },
    ];
    return actions;
  }

  appendQuickActions() {
    const wrapper = document.createElement('div');
    wrapper.className = 'c-chat_quick_actions';

    for (const action of this.getQuickActions()) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'c-chat_quick_action';

      const icon = document.createElement('span');
      icon.className = 'c-chat_quick_action_icon';
      icon.textContent = action.icon;

      const label = document.createTextNode(this.t(action.label, action.labelEn));

      btn.append(icon, label);
      btn.addEventListener('click', () => {
        const msg = this.t(action.message, action.messageEn);
        this.sendMessage(msg);
      });

      wrapper.appendChild(btn);
    }

    this.messagesContainer?.appendChild(wrapper);
  }

  handleSend() {
    if (!this.inputField || this.isWaitingForResponse || !this.concessionId) return;
    const text = this.inputField.value.trim();
    if (!text) return;

    this.inputField.value = '';
    this.sendMessage(text);
  }

  async sendMessage(text) {
    if (this.isWaitingForResponse || !this.concessionId) return;

    this.removeQuickActions();
    this.appendUserMessage(text, true);
    this.conversationHistory.push({ role: 'user', content: text });
    this.saveChatToStorage();

    this.isWaitingForResponse = true;
    this.updateInputAvailability();
    this.appendAgentTyping();
    this.scrollToBottom();

    try {
      const response = await this.callChatApi(this.conversationHistory);
      this.removeTypingIndicator();

      const hasForm = !!response.form;
      if (response.reply && !hasForm) {
        this.appendAgentMessage(response.reply, false, true);
      }

      if (response.vehicles && response.vehicles.length > 0 && !this.disableVehicleSearch) {
        this.appendVehicleResults(response.vehicles, true);
      }

      const pageLinks =
        Array.isArray(response.page_links) && response.page_links.length > 0
          ? response.page_links
          : response.page_link
            ? [response.page_link]
            : [];
      pageLinks.forEach((link) => this.appendPageLink(link, true));

      if (response.form) {
        this.appendContactForm(response.form, true);
      }

      const assistantHistoryContent =
        response.reply ||
        response.form?.message ||
        this.t('Je peux vous aider avec ça.', 'I can help you with that.');

      this.conversationHistory.push({
        role: 'assistant',
        content: assistantHistoryContent,
      });
      this.saveChatToStorage();
    } catch (error) {
      this.removeTypingIndicator();
      this.appendAgentMessage(
        this.t(
          "Désolé, une erreur s'est produite. Veuillez réessayer.",
          'Sorry, an error occurred. Please try again.'
        ),
        true,
        true
      );
    } finally {
      this.isWaitingForResponse = false;
      this.updateInputAvailability();
      this.scrollToBottom();
      this.inputField?.focus();
    }
  }

  async callChatApi(messages) {
    const url = `${this.apiBase}/wp-json/lpdg/v1/chat`;

    const body = {
      messages,
      concession_id: Number(this.concessionId),
    };

    if (this.mode === 'embed' || this.disableVehicleSearch) {
      body.mode = 'embed';
      if (this.inventoryUrlNew) {
        body.inventory_url_new = this.inventoryUrlNew;
      }
      if (this.inventoryUrlUsed) {
        body.inventory_url_used = this.inventoryUrlUsed;
      }
      // Backward-compatible single URL if only one is set.
      if (this.inventoryUrl && !this.inventoryUrlNew && !this.inventoryUrlUsed) {
        body.inventory_url = this.inventoryUrl;
      }
    }

    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      throw new Error(`API error: ${res.status}`);
    }

    return res.json();
  }

  setInputEnabled(enabled) {
    if (this.inputField) {
      this.inputField.disabled = !enabled;
    }
    if (this.sendButton) {
      this.sendButton.disabled = !enabled;
    }
  }

  getAgentAvatarSrc() {
    const img = this.querySelector('.c-chat_agent img');
    return img?.src || '';
  }

  createAgentAvatar() {
    const avatar = document.createElement('div');
    avatar.className = 'c-chat_messages_agent';
    const avatarImg = document.createElement('div');
    avatarImg.className = 'c-chat_avatar';
    avatarImg.innerHTML = `<img src="${this.escapeHtml(this.getAgentAvatarSrc())}" alt="Agent">`;
    avatar.appendChild(avatarImg);
    return avatar;
  }

  appendAgentMessage(text, isError = false, persist = false) {
    const group = document.createElement('div');
    group.className = 'c-chat_messages';
    group.appendChild(this.createAgentAvatar());

    const col = document.createElement('div');
    col.className = 'c-chat_messages_col';

    const msgEl = document.createElement('div');
    msgEl.className = `c-chat_message is-visible${isError ? ' c-chat_message--error' : ''}`;
    const inner = document.createElement('div');
    inner.className = 'c-chat_message_content_inner';
    const content = document.createElement('div');
    content.className = 'c-chat_message_content';
    content.textContent = text;

    inner.appendChild(content);
    msgEl.appendChild(inner);
    col.appendChild(msgEl);
    group.appendChild(col);
    this.messagesContainer?.appendChild(group);

    if (persist) {
      this.pushUiEntry({
        type: 'assistant',
        payload: text,
        isError,
      });
    }

    requestAnimationFrame(() => this.scrollToBottom());
  }

  appendUserMessage(text, persist = false) {
    const wrapper = document.createElement('div');
    wrapper.className = 'c-chat_message c-chat_message--user is-visible';

    const inner = document.createElement('div');
    inner.className = 'c-chat_message_content_inner';
    const content = document.createElement('div');
    content.className = 'c-chat_message_content c-chat_message_content--user';
    content.textContent = text;

    inner.appendChild(content);
    wrapper.appendChild(inner);
    this.messagesContainer?.appendChild(wrapper);

    if (persist) {
      this.pushUiEntry({
        type: 'user',
        payload: text,
      });
    }

    requestAnimationFrame(() => this.scrollToBottom());
  }

  appendAgentTyping() {
    const group = document.createElement('div');
    group.className = 'c-chat_messages c-chat_typing_group';
    group.appendChild(this.createAgentAvatar());

    const col = document.createElement('div');
    col.className = 'c-chat_messages_col';

    const msgEl = document.createElement('div');
    msgEl.className = 'c-chat_message c-chat_message_content_typing is-visible';
    const inner = document.createElement('div');
    inner.className = 'c-chat_message_content_inner';
    const content = document.createElement('div');
    content.className = 'c-chat_message_content c-chat_message_content--typing';

    for (let i = 0; i < 3; i++) {
      const dot = document.createElement('span');
      dot.className = 'c-chat_message_content_dot';
      content.appendChild(dot);
    }

    inner.appendChild(content);
    msgEl.appendChild(inner);
    col.appendChild(msgEl);
    group.appendChild(col);

    this.messagesContainer?.appendChild(group);
    requestAnimationFrame(() => this.scrollToBottom());
  }

  removeTypingIndicator() {
    const typing = this.messagesContainer?.querySelector('.c-chat_typing_group');
    typing?.remove();
  }

  removeQuickActions() {
    const qa = this.messagesContainer?.querySelector('.c-chat_quick_actions');
    qa?.remove();
  }

  appendVehicleResults(vehicles, persist = false) {
    const group = document.createElement('div');
    group.className = 'c-chat_messages';
    group.appendChild(this.createAgentAvatar());

    const col = document.createElement('div');
    col.className = 'c-chat_messages_col';

    const msgEl = document.createElement('div');
    msgEl.className = 'c-chat_message is-visible';
    const inner = document.createElement('div');
    inner.className = 'c-chat_message_content_inner';

    const swiperContainer = document.createElement('div');
    swiperContainer.className = 'c-chat_vehicle_results';

    const swiper = document.createElement('div');
    swiper.className = 'swiper c-chat_vehicle_swiper';

    const swiperWrapper = document.createElement('div');
    swiperWrapper.className = 'swiper-wrapper';

    vehicles.forEach((v) => swiperWrapper.appendChild(this.createVehicleSlide(v)));

    swiper.appendChild(swiperWrapper);
    swiperContainer.appendChild(swiper);

    const navContainer = document.createElement('div');
    navContainer.className = 'c-chat_vehicle_nav';

    const prevBtn = document.createElement('div');
    prevBtn.className = 'c-chat_swiper_prev';
    prevBtn.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>';

    const nextBtn = document.createElement('div');
    nextBtn.className = 'c-chat_swiper_next';
    nextBtn.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>';

    navContainer.append(prevBtn, nextBtn);
    swiperContainer.appendChild(navContainer);

    const viewAllBtn = document.createElement('a');
    viewAllBtn.href = this.inventoryUrl || '/inventaire/';
    viewAllBtn.className = 'c-chat_view_all_button';
    if (viewAllBtn.href.startsWith('http')) {
      viewAllBtn.target = '_blank';
      viewAllBtn.rel = 'noopener noreferrer';
    }
    viewAllBtn.textContent = this.t("Voir tout l'inventaire", 'View all inventory');
    swiperContainer.appendChild(viewAllBtn);

    inner.appendChild(swiperContainer);
    msgEl.appendChild(inner);
    col.appendChild(msgEl);
    group.appendChild(col);

    this.messagesContainer?.appendChild(group);
    if (persist) {
      this.pushUiEntry({
        type: 'vehicles',
        payload: vehicles,
      });
    }

    setTimeout(() => {
      this.initSwiper(swiper, prevBtn, nextBtn);
      this.scrollToBottom();
    }, 100);
  }

  createVehicleSlide(vehicle) {
    const slide = document.createElement('div');
    slide.className = 'swiper-slide';

    const photoUrl = vehicle.photo
      ? vehicle.photo.startsWith('http')
        ? vehicle.photo
        : `${window.location.origin}${vehicle.photo.startsWith('/') ? '' : '/'}${vehicle.photo}`
      : '';

    const price = this.formatNumber(vehicle.price);
    const makeModel = `${vehicle.make} ${vehicle.model}`;
    const trimYear = `${vehicle.trim || ''} ${vehicle.year || ''}`.trim();
    const kms = vehicle.kms ? `${this.formatNumber(vehicle.kms)} km` : '';

    slide.innerHTML = `
      <div class="c-chat_vehicle_slide">
        <a href="${this.escapeHtml(vehicle.permalink)}" class="c-chat_vehicle_link">
          <div class="c-chat_vehicle_image">
            ${
              photoUrl
                ? `<img src="${this.escapeHtml(photoUrl)}" alt="${this.escapeHtml(makeModel)}" loading="lazy" onerror="this.style.display='none'">`
                : ''
            }
          </div>
          <div class="c-chat_vehicle_body">
            <h3 class="c-chat_vehicle_title">${this.escapeHtml(makeModel)}</h3>
            ${trimYear ? `<div class="c-chat_vehicle_meta">${this.escapeHtml(trimYear)}</div>` : ''}
            ${kms ? `<div class="c-chat_vehicle_meta">${this.escapeHtml(kms)}</div>` : ''}
            <div class="c-chat_vehicle_price">${price} $</div>
            ${
              vehicle.stocknumber
                ? `<div class="c-chat_vehicle_stock">Stock ${this.escapeHtml(vehicle.stocknumber)}</div>`
                : ''
            }
          </div>
        </a>
        <div class="c-chat_vehicle_actions">
          <a href="${this.escapeHtml(vehicle.permalink)}" class="c-chat_vehicle_button">
            ${this.t('Voir la fiche', 'View details')}
          </a>
        </div>
      </div>
    `;

    return slide;
  }

  initSwiper(el, prevBtn, nextBtn) {
    if (this.vehicleResultsSwiper) {
      this.vehicleResultsSwiper.destroy(true, true);
    }
    try {
      this.vehicleResultsSwiper = new Swiper(el, {
        modules: [Navigation],
        slidesPerView: 1,
        spaceBetween: 6,
        allowTouchMove: true,
        grabCursor: true,
        navigation: { nextEl: nextBtn, prevEl: prevBtn },
      });
    } catch (err) {
      console.error('Swiper init error:', err);
    }
  }

  appendPageLink(link, persist = false) {
    const wrapper = document.createElement('div');
    wrapper.className = 'c-chat_messages';
    wrapper.appendChild(this.createAgentAvatar());

    const col = document.createElement('div');
    col.className = 'c-chat_messages_col';

    const msgEl = document.createElement('div');
    msgEl.className = 'c-chat_message is-visible';
    const inner = document.createElement('div');
    inner.className = 'c-chat_message_content_inner';

    const a = document.createElement('a');
    a.href = link.url;
    a.className = 'c-chat_message_content c-chat_page_link';
    const iconSpan = document.createElement('span');
    iconSpan.className = 'c-chat_page_link_icon';
    iconSpan.textContent = '🔗';
    a.append(iconSpan, document.createTextNode(link.title));

    inner.appendChild(a);
    msgEl.appendChild(inner);
    col.appendChild(msgEl);
    wrapper.appendChild(col);

    this.messagesContainer?.appendChild(wrapper);
    if (persist) {
      this.pushUiEntry({
        type: 'page_link',
        payload: link,
      });
    }
    requestAnimationFrame(() => this.scrollToBottom());
  }

  appendContactForm(formData, persist = false) {
    const formLabels = {
      ventes: { fr: 'Rendez-vous ventes', en: 'Sales appointment' },
      service: { fr: 'Rendez-vous service', en: 'Service appointment' },
      financement: { fr: 'Demande de financement', en: 'Financing request' },
      evaluation: { fr: "Évaluation d'échange", en: 'Trade-in evaluation' },
      contact: { fr: 'Nous contacter', en: 'Contact us' },
    };

    const formLabel = formLabels[formData.form_type] || formLabels.contact;

    const group = document.createElement('div');
    group.className = 'c-chat_messages';
    group.appendChild(this.createAgentAvatar());

    const col = document.createElement('div');
    col.className = 'c-chat_messages_col';

    const msgEl = document.createElement('div');
    msgEl.className = 'c-chat_message is-visible c-chat_message--form';
    const inner = document.createElement('div');
    inner.className = 'c-chat_message_content_inner';

    const form = document.createElement('form');
    form.className = 'c-chat_form';

    const formContextMessage =
      formData.message?.trim() || this.getDefaultFormContextMessage(formData.form_type);
    const header = document.createElement('div');
    header.className = 'c-chat_form_header';
    header.textContent = formContextMessage;
    form.appendChild(header);

    const fields = [
      { name: 'name', label: 'Nom', labelEn: 'Name', type: 'text' },
      { name: 'email', label: 'Courriel', labelEn: 'Email', type: 'email' },
      { name: 'phone', label: 'Téléphone', labelEn: 'Phone', type: 'tel' },
    ];

    fields.forEach((f) => {
      const fieldWrap = document.createElement('div');
      fieldWrap.className = 'c-chat_form_field';

      const labelEl = document.createElement('label');
      labelEl.setAttribute('for', `chat-${f.name}`);
      labelEl.className = 'c-chat_form_label';
      labelEl.textContent = this.t(f.label, f.labelEn);

      const input = document.createElement('input');
      input.type = f.type;
      input.id = `chat-${f.name}`;
      input.name = f.name;
      input.required = true;
      input.className = 'c-chat_form_input';

      fieldWrap.append(labelEl, input);
      form.appendChild(fieldWrap);
    });

    if (formData.form_type === 'ventes') {
      const inventoryWrap = document.createElement('fieldset');
      inventoryWrap.className = 'c-chat_form_field c-chat_form_field--radio';

      const legend = document.createElement('legend');
      legend.className = 'c-chat_form_label';
      legend.textContent = this.t(
        'Type de véhicule recherché',
        "Vehicle type you're looking for"
      );
      inventoryWrap.appendChild(legend);

      const options = [
        { value: 'new', label: 'Neuf', labelEn: 'New' },
        { value: 'used', label: 'Usagé', labelEn: 'Used' },
      ];

      options.forEach((option, index) => {
        const optionLabel = document.createElement('label');
        optionLabel.className = 'c-chat_form_radio_label';

        const radio = document.createElement('input');
        radio.type = 'radio';
        radio.name = 'inventory';
        radio.value = option.value;
        radio.required = index === 0;
        radio.className = 'c-chat_form_radio';
        radio.id = `chat-inventory-${option.value}`;

        const optionText = document.createElement('span');
        optionText.textContent = this.t(option.label, option.labelEn);

        optionLabel.append(radio, optionText);
        inventoryWrap.appendChild(optionLabel);
      });

      form.appendChild(inventoryWrap);
    }

    const submitBtn = document.createElement('button');
    submitBtn.type = 'submit';
    submitBtn.className = 'c-chat_form_submit';
    submitBtn.textContent = this.t('Soumettre', 'Submit');
    form.appendChild(submitBtn);

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.handleFormSubmit(form, formData.form_type, formLabel);
    });

    inner.appendChild(form);
    msgEl.appendChild(inner);
    col.appendChild(msgEl);
    group.appendChild(col);

    this.messagesContainer?.appendChild(group);
    if (persist) {
      this.pushUiEntry({
        type: 'form',
        payload: formData,
      });
    }
    requestAnimationFrame(() => this.scrollToBottom());
  }

  mapFormulaire(formType, inventory) {
    if (formType === 'ventes') {
      return inventory === 'new' ? 'neuf' : 'occasion';
    }
    if (formType === 'service') return 'Services';
    if (formType === 'financement') return 'Crédit';
    if (formType === 'evaluation') return 'Évaluation';
    return 'Contact chat';
  }

  async handleFormSubmit(form, formType, formLabel) {
    const fd = new FormData(form);
    const data = {};
    fd.forEach((v, k) => {
      data[k] = v.toString();
    });

    const submitBtn = form.querySelector('button[type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = this.t('Envoi...', 'Sending...');
    }

    try {
      const payload = {
        Concessionnaire: this.concessionId,
        formulaire: this.mapFormulaire(formType, data.inventory),
        Nom: data.name || '',
        Courriel: data.email || '',
        Téléphone: data.phone || '',
        'Page actuelle': window.location.href,
        Langue: this.getCurrentLocale(),
        Source: this.mode === 'embed' ? 'AI Chat Embed' : 'AI Chat',
      };

      if (formType === 'ventes' && (data.inventory === 'new' || data.inventory === 'used')) {
        payload.inventory = data.inventory;
      }

      let res;
      if (this.mode === 'embed') {
        res = await fetch(`${this.apiBase}/wp-json/lpdg/v1/chat-lead`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        const formPayload = new FormData();
        formPayload.append('lpdgform', '1');
        Object.keys(payload).forEach((key) => {
          formPayload.append(key, payload[key]);
        });
        res = await fetch('/', {
          method: 'POST',
          credentials: 'same-origin',
          body: formPayload,
        });
      }

      const formWrapper = form.closest('.c-chat_message');
      if (formWrapper) formWrapper.style.display = 'none';

      if (res.ok) {
        this.appendAgentMessage(
          this.t(
            'Merci ! Votre demande a été envoyée avec succès. Nous vous contacterons bientôt.',
            'Thank you! Your request has been sent successfully. We will contact you soon.'
          ),
          false,
          true
        );
      } else {
        this.appendAgentMessage(
          this.t(
            "Désolé, une erreur est survenue lors de l'envoi. Veuillez réessayer.",
            'Sorry, an error occurred. Please try again.'
          ),
          true,
          true
        );
      }
    } catch (error) {
      this.appendAgentMessage(
        this.t(
          "Désolé, une erreur est survenue lors de l'envoi. Veuillez réessayer.",
          'Sorry, an error occurred. Please try again.'
        ),
        true,
        true
      );
    }

    this.scrollToBottom();
  }

  getDefaultFormContextMessage(formType) {
    const fallbackMessages = {
      ventes: {
        fr: "Voici le formulaire pour votre rendez-vous d'achat de véhicule.",
        en: 'Here is the form for your vehicle purchase appointment.',
      },
      service: {
        fr: 'Voici le formulaire pour votre rendez-vous de service.',
        en: 'Here is the form for your service appointment.',
      },
      financement: {
        fr: 'Voici le formulaire pour votre demande de financement.',
        en: 'Here is the form for your financing request.',
      },
      evaluation: {
        fr: "Voici le formulaire pour l'évaluation de votre échange.",
        en: 'Here is the form for your trade-in evaluation.',
      },
      contact: {
        fr: 'Voici le formulaire pour nous contacter.',
        en: 'Here is the contact form.',
      },
    };

    const message = fallbackMessages[formType] || fallbackMessages.contact;
    return this.t(message.fr, message.en);
  }

  getStorageKey() {
    return `lpdg-chat-history:${window.location.host}:${this.concessionId || 'none'}`;
  }

  pushUiEntry(entry) {
    this.uiHistory.push(entry);
    this.saveChatToStorage();
  }

  saveChatToStorage() {
    if (!this.concessionId) return;
    try {
      const payload = {
        version: this.storageVersion,
        conversationHistory: this.conversationHistory,
        uiHistory: this.uiHistory,
        updatedAt: Date.now(),
      };
      window.localStorage.setItem(this.getStorageKey(), JSON.stringify(payload));
    } catch (error) {
      console.warn('Unable to save chat history to localStorage', error);
    }
  }

  clearChatStorage() {
    if (!this.concessionId) return;
    try {
      window.localStorage.removeItem(this.getStorageKey());
    } catch (error) {
      console.warn('Unable to clear chat history from localStorage', error);
    }
  }

  restoreChatFromStorage() {
    if (!this.messagesContainer || !this.concessionId) {
      return;
    }

    try {
      const raw = window.localStorage.getItem(this.getStorageKey());
      if (!raw) {
        return;
      }

      const parsed = JSON.parse(raw);
      if (!parsed || parsed.version !== this.storageVersion) {
        return;
      }

      this.conversationHistory = Array.isArray(parsed.conversationHistory)
        ? parsed.conversationHistory
        : [];
      this.uiHistory = Array.isArray(parsed.uiHistory) ? parsed.uiHistory : [];

      if (!this.uiHistory.length) {
        return;
      }

      this.messagesContainer.innerHTML = '';
      this.firstOpen = false;

      for (const entry of this.uiHistory) {
        switch (entry.type) {
          case 'user':
            this.appendUserMessage(String(entry.payload || ''), false);
            break;
          case 'assistant':
            this.appendAgentMessage(String(entry.payload || ''), !!entry.isError, false);
            break;
          case 'vehicles':
            if (!this.disableVehicleSearch) {
              this.appendVehicleResults(entry.payload || [], false);
            }
            break;
          case 'page_link':
            this.appendPageLink(entry.payload || {}, false);
            break;
          case 'form':
            this.appendContactForm(entry.payload || {}, false);
            break;
          default:
            break;
        }
      }
    } catch (error) {
      console.warn('Unable to restore chat history from localStorage', error);
    }
  }

  scrollToBottom() {
    if (!this.contentBody) return;
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.contentBody.scrollTo({
          top: this.contentBody.scrollHeight,
          behavior: 'smooth',
        });
      }, 100);
    });
  }

  formatNumber(num) {
    return Math.round(num || 0)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }

  escapeHtml(text) {
    if (text === null || text === undefined) return '';
    const div = document.createElement('div');
    div.textContent = String(text);
    return div.innerHTML;
  }
}

if (!customElements.get('c-chat')) {
  customElements.define('c-chat', Chat);
}
